/**
 * Aviso no mapa enquanto o quadro na tela está num trecho em que a NOAA não
 * publicou a camada de base (loop/semNoaa.ts): a imagem do satélite falta,
 * mas focos, fumaça, estações, rios, navios e as outras imagens do GOES seguem
 * no horário do quadro — o mapa sem a base não pode parecer defeito.
 *
 * Fica sob a fileira de chips do topo, no meio, sem botão nem clique (não
 * cobre nada que se toque), entre o trilho e a coluna do celular; some sozinho
 * quando o quadro volta a ter imagem.
 */
import { TriangleAlert } from 'lucide-react';
import { formatDayTimeBrt } from '../lib/time';
import { AVISO_ATRASO, mensagemAtrasoDaNoaa } from '../loop/autoRefresh';
import { useGoesStore } from '../state/goes';
import { useToastStore } from '../state/toasts';
import { useSemBaseNoQuadro } from './hooks';
import { CHIP } from './MapActions';

export function AvisoSemSatelite(props: { mobile: boolean }) {
  const semBase = useSemBaseNoQuadro();
  const baseVisivel = useGoesStore((s) => !!s.layers[0]?.visible);
  // Durante os 15 s do aviso de atraso (autoRefresh.ts), que diz o mesmo, este espera: os dois se encobriam.
  const avisoNaTela = useToastStore((s) => s.toasts.some((t) => t.id === AVISO_ATRASO));
  if (!semBase || !baseVisivel || avisoNaTela) return null;
  const desde = formatDayTimeBrt(semBase.desde);
  return (
    <div
      className={`pointer-events-none absolute inset-x-0 z-10 flex justify-center ${props.mobile ? 'px-[60px]' : 'px-14'}`}
      style={{ top: props.mobile ? 'calc(env(safe-area-inset-top) + 64px)' : 56 }}
    >
      <p
        className={`${CHIP} flex max-w-md items-start gap-2 rounded-xl px-3 py-1.5 text-xs leading-snug`}
        role="status"
        title={
          semBase.noFim
            ? mensagemAtrasoDaNoaa(`${desde} (Brasília)`, semBase.produto)
            : `A NOAA não publicou “${semBase.produto}” entre ${desde} e ${formatDayTimeBrt(semBase.ate)} (Brasília). As outras camadas seguem no horário.`
        }
        data-aviso-sem-satelite=""
      >
        <TriangleAlert className="mt-px size-3.5 shrink-0 text-yellow-300" aria-hidden />
        <span>
          <span className="font-medium text-yellow-200">
            {semBase.noFim ? `${semBase.produto}: parada na NOAA desde ${desde}.` : `${semBase.produto}: a NOAA não publicou este horário.`}
          </span>
          {/* Celular deitado: uma linha só, para não descer sobre a legenda. */}
          <span className="[@media(max-height:500px)]:hidden"> As outras camadas seguem neste horário; ao fundo, a foto fixa do Sentinel-2 (EOxCloudless).</span>
        </span>
      </p>
    </div>
  );
}
